import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { AuthService } from '../auth/auth.service';
import { WatchTimeService } from './watch-time.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AudioService {

  private apiData = new BehaviorSubject<any>(null);
  public apiData$ = this.apiData.asObservable();

  private songData = new BehaviorSubject<any>(null);
  public songData$ = this.songData.asObservable();

  private newState = new BehaviorSubject<any>(null);
  public newState$ = this.newState.asObservable();

  private songStatus = new BehaviorSubject<any>(null);
  public songState$ = this.songStatus.asObservable();

  private opencloseplayer = new BehaviorSubject<any>(null);
  public opencloseplayer$ = this.opencloseplayer.asObservable();

  private queueSongClear = new BehaviorSubject<any>(null);
  public queueSongClear$ = this.queueSongClear.asObservable();

  private queuePopUpClose = new BehaviorSubject<any>(null);
  public queuePopUpClose$ = this.queuePopUpClose.asObservable();

  public returnCurrentplayingSongIdVar: any;
  private currentplayingSongIdVar = new BehaviorSubject<any>(null);
  public currentplayingSongIdVar$ = this.currentplayingSongIdVar.asObservable();

  public returnCurrentplayingRadioIdVar: any;
  private currentplayingRadioIdVar = new BehaviorSubject<any>(null);
  public currentplayingRadioIdVar$ = this.currentplayingRadioIdVar.asObservable();

  private setFavVar = new BehaviorSubject<any>(null);
  public setFavVar$ = this.setFavVar.asObservable();

  private playerProgressVar = new BehaviorSubject<any>(null);
  public playerProgressVar$ = this.playerProgressVar.asObservable();

  public player;
  public audioPlayer;
  public queueData;

  // ── Free-listening limit (v2/customer/subscription) ─────────────
  // `free_minutes_audio_limit` is in MINUTES, `audio_played_time` in SECONDS —
  // everything below is normalised to seconds.
  //
  // `serverPlayedTime` is the last value we know the server holds and
  // `pendingSeconds` is real playback not yet posted, so the running total is
  // always the sum of the two. Checkpointing pendingSeconds back to 0 on a
  // successful post is what makes refreshing the server value safe at any
  // moment (including after the 00:50 daily reset scheduler wipes it).
  private isSubscribed     = false;
  private freeLimitSeconds = 0;
  private serverPlayedTime = 0;
  private pendingSeconds   = 0;
  private limitLoaded      = false;
  private limitLoading     = false;
  private reportInFlight   = false;
  private lastRefreshAt    = 0;
  private lastReportedTotal = -1;

  /** Re-pull the server figures if they are older than this (ms). */
  private readonly REFRESH_AFTER_MS = 5 * 60 * 1000;

  // Raised when a non-subscriber runs out of free minutes — either mid-playback
  // or when they try to start something new. The audio player renders the
  // Subscribe / Cancel dialog, so both routes show the same prompt.
  private limitPopup = new BehaviorSubject<boolean>(false);
  public limitPopup$ = this.limitPopup.asObservable();

  constructor(
    private apiService:  ApiService,
    private userService: UserService,
    private authService: AuthService,
    private router:      Router,
    private watchTime:   WatchTimeService
  ) { }
  setData(data) {
    this.apiData.next(data);
  }
  setStatus(data) {
    this.songData.next(data);
  }
  newStateSong(status) {
    this.newState.next(status);
  }
  songState(status) {
    this.songStatus.next(status);
  }
  queueClear(status) {
    this.queueSongClear.next(status);
  }
  queueClose(status) {
    this.queuePopUpClose.next(status);
  }
  setCurrentPlayingSongId(id) {
    this.currentplayingSongIdVar.next(id);
    this.returnCurrentplayingSongIdVar = id;
  }

  getCurrentPlayingSongId() {
    return this.returnCurrentplayingSongIdVar;
  }

  setCurrentPlayingRadioId(id) {
    this.currentplayingRadioIdVar.next(id);
    this.returnCurrentplayingRadioIdVar = id;
  }

  getCurrentPlayingRadioId() {
    return this.returnCurrentplayingRadioIdVar;
  }

  triggerSongFavourite(data) {
    this.setFavVar.next(data);
  }

  updatePlayerProgress(progress) {
    this.playerProgressVar.next(progress);
  }

  setPlayerVariable(data) {
    this.player = data;
  }

  getPlayerVariable() {
    return this.player;
  }

  /**
   * The one live AudioPlayerComponent (rendered once, in the footer).
   *
   * A page that wants to show transport controls of its own — the track page —
   * drives this instance instead of building a second <audio>, so there is still
   * only ever one thing playing. Deliberately separate from the player/
   * setPlayerVariable pair above, which holds the older footer player object.
   */
  setAudioPlayer(component) {
    this.audioPlayer = component;
  }

  getAudioPlayer() {
    return this.audioPlayer;
  }

  setQueueData(data) {
    this.queueData = data;
  }

  getQueueData() {
    return this.queueData;
  }

  openOrClose(data){
    return this.opencloseplayer.next(data);
  }

  // ─────────────────────────────────────────────────────────────
  // Free-listening limit + played-time reporting
  // ─────────────────────────────────────────────────────────────

  /**
   * Pulls is_subscribed / free_minutes_audio_limit / audio_played_time.
   * Skipped while a report is in flight so we never read a value the server is
   * still being told about. `force` bypasses the staleness window.
   */
  refreshAudioLimit(force = false) {
    if (!localStorage.getItem('currentUser')) { return; }
    if (this.limitLoading || this.reportInFlight) { return; }
    if (!force && this.limitLoaded &&
        (Date.now() - this.lastRefreshAt) < this.REFRESH_AFTER_MS) { return; }

    this.limitLoading = true;
    this.apiService.callGetAPI('v2/customer/subscription', environment.userApiUrl)
      .subscribe(
        data => {
          const res = (data && data['response']) ? data['response'] : {};
          this.isSubscribed     = Number(res['is_subscribed']) === 1;
          this.freeLimitSeconds = Math.max(0, Number(res['free_minutes_audio_limit']) || 0) * 60;
          this.serverPlayedTime = Math.max(0, Number(res['audio_played_time']) || 0);
          this.limitLoading     = false;
          this.limitLoaded      = true;
          this.lastRefreshAt    = Date.now();
        },
        err => {
          // Leave the previous figures in place — a failed refresh must not
          // silently unlock unlimited playback or lock out a paying user.
          console.warn('[AudioService] audio limit refresh failed', err);
          this.limitLoading = false;
        });
  }

  /** Real seconds listened, including what has not been posted yet. */
  totalPlayedSeconds(): number {
    return this.serverPlayedTime + this.pendingSeconds;
  }

  /** Seconds of free listening still available (Infinity for subscribers). */
  remainingFreeSeconds(): number {
    if (this.isSubscribed) { return Infinity; }
    return Math.max(0, this.freeLimitSeconds - this.totalPlayedSeconds());
  }

  /**
   * True once a non-subscriber has used up the free allowance. Until the figures
   * have loaded we report false, so playback is never blocked on a slow request
   * — the player re-checks on every timeupdate and stops as soon as they land.
   */
  isLimitReached(): boolean {
    if (this.isSubscribed || !this.limitLoaded) { return false; }
    if (this.freeLimitSeconds <= 0) { return false; }
    return this.totalPlayedSeconds() >= this.freeLimitSeconds;
  }

  /**
   * The single audio play gate, shared by every listing/detail page.
   * Not signed in → sign-in popup. Subscribed → allowed. Otherwise allowed for
   * as long as free minutes remain, and prompted to subscribe once they are gone.
   */
  canPlayAudio(): boolean {
    if (!localStorage.getItem('currentUser')) {
      this.userService.displayPopup('sign-in');
      return false;
    }

    // Keep the figures warm for the next check; the decision below uses whatever
    // is already cached so this stays synchronous for existing callers.
    this.refreshAudioLimit();

    if (this.isSubscribedUser()) { return true; }
    if (!this.isLimitReached())  { return true; }

    this.openLimitPopup();
    return false;
  }

  openLimitPopup() {
    this.limitPopup.next(true);
  }

  closeLimitPopup() {
    this.limitPopup.next(false);
  }

  /**
   * Falls back to the cached login payload until the subscription call has
   * landed, so a subscriber is never bounced to /subscription on a cold start.
   */
  private isSubscribedUser(): boolean {
    if (this.limitLoaded) { return this.isSubscribed; }
    const currentUser = this.authService.decryption(localStorage.getItem('currentUser'));
    return !!(currentUser && currentUser['response'] && currentUser['response'].is_subscribed);
  }

  redirectToSubscription() {
    this.router.navigate(['/subscription']);
  }

  /**
   * Called by the player for each chunk of genuine (non-seek) playback. Feeds
   * both the free-limit counter and the per-day listening total
   * (customer_daily_watch_time via media-api watch-time).
   */
  addPlayedSeconds(delta: number) {
    if (!delta || delta <= 0) { return; }
    this.pendingSeconds += delta;
    this.watchTime.add('audio', delta);
  }

  /**
   * Posts the cumulative total to store_customer_audio_played_time. The backend
   * assigns rather than increments, so we always send server + pending, and on
   * success we move pending into serverPlayedTime (the checkpoint) instead of
   * letting it grow unbounded.
   */
  reportPlayedTime() {
    // Same trigger points (pause, track change, unload) checkpoint the per-day
    // listening total; it keeps its own pending counter and in-flight guard.
    this.watchTime.flush('audio');

    if (!localStorage.getItem('currentUser')) { return; }
    if (this.reportInFlight || this.pendingSeconds <= 0) { return; }

    const total = Math.floor(this.serverPlayedTime + this.pendingSeconds);
    if (total <= 0 || total === this.lastReportedTotal) { return; }

    // Commit whole seconds only; the sub-second remainder stays pending so it is
    // not silently dropped once per report (that drift adds up over a session).
    const committed = total - this.serverPlayedTime;
    this.reportInFlight    = true;
    this.lastReportedTotal = total;

    this.apiService.callPostAPI('v2/store_customer_audio_played_time', {
      audio_played_time: total
    }, environment.userApiUrl).subscribe(
      () => {
        this.serverPlayedTime = total;
        // Only drop what was actually sent — playback during the request keeps
        // accumulating and must survive the checkpoint.
        this.pendingSeconds   = Math.max(0, this.pendingSeconds - committed);
        this.reportInFlight   = false;
      },
      err => {
        // Keep pendingSeconds so the next trigger retries with the full total.
        // Silent: this fires on every pause/track change, and a cancelled request
        // during unload must not raise an error toast at the user.
        console.warn('[AudioService] played-time report failed', err);
        this.lastReportedTotal = -1;
        this.reportInFlight    = false;
      }
    );
  }
}
